import {
  Arg,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { TYPOGRAPHY_BASE_CLASS } from '../../../utils/spds/constants/typography';
import Container from '../core/container.gjs';
import Super from '../core/base.gjs';
import styles from './base.module.scss';

const METADATA = Metadata.define({
  description: `
    Shared base for every SPDS Typography component. It composes \`Spds::Core::Container\`,
    renders a configurable HTML tag (defaulting to \`p\`) via \`@tag\`, and exposes the shared
    \`@content\` argument along with the base system font family.

    Role components (\`Display\`, \`Heading\`, \`Body\`, \`Caption\`, \`Subtitle\`) extend this base
    to layer on their weight, size, and line-height styles, so each concrete component stays a
    thin wrapper with no repeated template or tag-handling logic.
  `,
  extends: [
    pick(CONTAINER_ARGS, [{ name: 'tag', default: 'p' }]),
    pick(CONTENT_ARGS, ['content']),
    pick(COMPONENT_STATE_ARGS, ['error']),
  ],
  meta: {
    srOnly: Arg.boolean({
      description: 'Visually hides the text while keeping it accessible to screen readers',
    }),
    strong: Arg.boolean({ description: 'Render the text with semibold weight' }),
  },
  name: 'Typography',
  // Typography components are not tooltip anchors, so drop the tooltip args inherited from core/base.
  omits: ['tooltipContent', 'tooltipOffset', 'tooltipPlacement', 'tooltipTitle'],
});

export default class SpdsTypographyBase extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = TYPOGRAPHY_BASE_CLASS;
  baseStyles = styles;

  get classesToMap() {
    return ['error', 'size', 'srOnly', 'strong'];
  }

  get inline() {
    return this.argOrDefault('inline');
  }

  get tag() {
    return this.argOrDefault('tag');
  }

  <template>
    <Container
      @content={{@content}}
      @flexbox={{false}}
      @id={{this.id}}
      @inline={{this.inline}}
      @tag={{this.tag}}
      class={{this.classes}}
      data-test-spds-typography={{this.componentName}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield}}
    </Container>
  </template>
}

export const TYPOGRAPHY_BASE_METADATA = SpdsTypographyBase.metadata;
